import { object, string, number, union, literal, safeParse } from 'valibot';
import { currencyCodeCache } from '../context/LocaleContext.jsx';
import { logError, logInfo } from './logging.js';

/**
 * @typedef {Object} BaseEventData
 * @property {string | undefined | null} productId
 * @property {string | undefined | null} productName
 * @property {string | undefined | null} productCategory
 * @property {string | undefined | null} productVariant
 * @property {number | undefined | null} productPrice
 * @property {string | undefined | null} personalisationType
 */

/**
 * @typedef {BaseEventData & {totalSteps: number | undefined | null}} PersonalisationStartData
 * @typedef {BaseEventData & {totalSteps: number, stepNumber: number | undefined | null, stepName: string | undefined | null}} PersonalisationStepCompleteData
 * @typedef {BaseEventData & {totalSteps: number, totalPrice: number | undefined | null}} PersonalisationCompleteData
 * @typedef {BaseEventData & {slotName: string | undefined | null, slotType: string | undefined | null, slotPosition: number | undefined | null, slotValue: string | undefined | null}} SlotPopulatedData
 * @typedef {BaseEventData & {stepNumber: number | undefined | null, stepName: string | undefined | null, slotName: string | undefined | null, slotType: string | undefined | null, slotPosition: string | undefined | null}} SlotRemovedData
 * @typedef {BaseEventData & {addOnName: string | undefined | null, addOnType: string | undefined | null, stepNumber: number | undefined | null, stepName: string | undefined | null}} AddOnAddedData
 * @typedef {BaseEventData & {addOnName: string | undefined | null, addOnType: string | undefined | null, stepNumber: number | undefined | null, stepName: string | undefined | null}} AddOnRemovedData
 * @typedef {BaseEventData & {variantPrice: number | undefined | null, variantId: string | undefined | null, variantTitle: string | undefined | null}} VariantSelectedData
 */

/**
 * @typedef {PersonalisationStartData | PersonalisationStepCompleteData | PersonalisationCompleteData | SlotPopulatedData | SlotRemovedData | AddOnAddedData | AddOnRemovedData | VariantSelectedData} EventData
 */

// Base schema for common event data properties.
const baseEventSchema = object({
  productId: number(),
  productName: string(),
  productCategory: string(),
  productVariant: string(),
  productPrice: number(),
  personalisationType: string()
});

// Event type schema.
const eventTypeSchema = union([
  literal('personalisation_start'),
  literal('personalisation_step_complete'),
  literal('personalisation_complete'),
  literal('slot_populated'),
  literal('slot_removed'),
  literal('add_on_added'),
  literal('add_on_removed'),
  literal('variant_selected')
]);

// Schema mapping for each event type.
const eventSchemas = {
  personalisation_start: object({
    ...baseEventSchema.entries,
    totalSteps: number()
  }),
  personalisation_step_complete: object({
    ...baseEventSchema.entries,
    totalSteps: number(),
    stepNumber: number(),
    stepName: string()
  }),
  personalisation_complete: object({
    ...baseEventSchema.entries,
    totalSteps: number(),
    totalPrice: number()
  }),
  slot_populated: object({
    ...baseEventSchema.entries,
    stepNumber: number(),
    stepName: string(),
    slotName: string(),
    slotType: string(),
    slotPosition: number(),
    slotValue: string()
  }),
  slot_removed: object({
    ...baseEventSchema.entries,
    stepNumber: number(),
    stepName: string(),
    slotName: string(),
    slotType: string(),
    slotPosition: number()
  }),
  add_on_added: object({
    ...baseEventSchema.entries,
    addOnName: string(),
    addOnType: string(),
    stepNumber: number(),
    stepName: string()
  }),
  add_on_removed: object({
    ...baseEventSchema.entries,
    addOnName: string(),
    addOnType: string(),
    stepNumber: number(),
    stepName: string()
  }),
  variant_selected: object({
    ...baseEventSchema.entries,
    variantPrice: number(),
    variantId: string(),
    variantTitle: string()
  })
};

// Event data transformers for converting validated data to dataLayer format.
const eventTransformers = {
  personalisation_start: (data) => ({
    total_steps: /** @type {PersonalisationStartData} */ (data).totalSteps
  }),
  variant_selected: (data) => ({
    variant_price: /** @type {VariantSelectedData} */ (data).variantPrice,
    variant_id: /** @type {VariantSelectedData} */ (data).variantId,
    variant_title: /** @type {VariantSelectedData} */ (data).variantTitle
  }),
  personalisation_step_complete: (data) => ({
    step_number: /** @type {PersonalisationStepCompleteData} */ (data).stepNumber,
    step_name: /** @type {PersonalisationStepCompleteData} */ (data).stepName,
    total_steps: /** @type {PersonalisationStepCompleteData} */ (data).totalSteps
  }),
  slot_populated: (data) => ({
    slot_name: /** @type {SlotPopulatedData} */ (data).slotName,
    slot_type: /** @type {SlotPopulatedData} */ (data).slotType,
    slot_position: /** @type {SlotPopulatedData} */ (data).slotPosition,
    slot_value: /** @type {SlotPopulatedData} */ (data).slotValue
  }),
  slot_removed: (data) => ({
    step_number: /** @type {SlotRemovedData} */ (data).stepNumber,
    step_name: /** @type {SlotRemovedData} */ (data).stepName,
    slot_name: /** @type {SlotRemovedData} */ (data).slotName,
    slot_type: /** @type {SlotRemovedData} */ (data).slotType,
    slot_position: /** @type {SlotRemovedData} */ (data).slotPosition
  }),
  add_on_added: (data) => ({
    add_on_name: /** @type {AddOnAddedData} */ (data).addOnName,
    add_on_product_id: /** @type {AddOnAddedData} */ (data).addOnType,
    step_number: /** @type {AddOnAddedData} */ (data).stepNumber,
    step_name: /** @type {AddOnAddedData} */ (data).stepName
  }),
  add_on_removed: (data) => ({
    add_on_name: /** @type {AddOnRemovedData} */ (data).addOnName,
    add_on_product_id: /** @type {AddOnRemovedData} */ (data).addOnType,
    step_number: /** @type {AddOnRemovedData} */ (data).stepNumber,
    step_name: /** @type {AddOnRemovedData} */ (data).stepName
  }),
  personalisation_complete: (data) => ({
    total_steps: /** @type {PersonalisationCompleteData} */ (data).totalSteps,
    total_price: /** @type {PersonalisationCompleteData} */ (data).totalPrice
  })
};

/**
 * Validates event data against the appropriate schema.
 * @param {Object} params - The parameters object.
 * @param {string} params.eventType - The type of event being validated.
 * @param {EventData} params.data - The data to validate.
 * @returns {import('valibot').SafeParseResult<any>} - The validation result.
 * @throws {Error} - If validation fails.
 */
const validateEventData = ({ eventType, data }) => {
  try {
    // Validate event type first.
    const eventTypeResult = safeParse(eventTypeSchema, eventType);
    if (!eventTypeResult.success) {
      throw new Error(`Invalid event type: ${eventType}`);
    }

    const validatedEventType = eventTypeResult.output;

    // Get the appropriate schema and validate data.
    const schema = eventSchemas[`${validatedEventType}`];
    if (!schema) {
      throw new Error(`Invalid event type: ${eventType}`);
    }

    const result = safeParse(schema, data);
    return result;
  } catch (error) {
    logError({
      label: 'Tracking',
      error,
      message: `Validation failed for event type ${eventType}: ${error.message}`,
      extraData: {
        eventType,
        data
      }
    });
    throw error;
  }
};

/**
 * Creates base event data object with common properties.
 * @param {EventData} validatedData - The validated event data.
 * @returns {Object} - Base event data object.
 */
const createBaseEventData = (validatedData) => ({
  item_id: validatedData.productId,
  item_name: validatedData.productName,
  item_category: validatedData.productCategory,
  item_variant: validatedData.productVariant,
  price: validatedData.productPrice,
  currency: currencyCodeCache,
  personalisation_type: validatedData.personalisationType
});

/**
 * Global product context for tracking.
 * @type {Object|null}
 */
let globalProductContext = null;

/**
 * Sets the global product context for tracking events.
 * @param {Object} product - The product object containing base information.
 * @param {string} product.id - The product ID.
 * @param {string} product.title - The product title.
 * @param {string} product.category - The product category.
 * @param {number} product.price - The product price.
 */
export const setProductContext = (product) => {
  globalProductContext = {
    productId: product.id,
    productName: product.title,
    productCategory: product.category || 'Unknown',
    productPrice: product.price,
    personalisationType: 'product'
  };
};

/**
 * Creates event data by merging global product context with specific event data.
 * @param {Object} eventSpecificData - Event-specific data.
 * @returns {EventData} - Complete event data.
 */
const createEventData = (eventSpecificData) => {
  if (!globalProductContext) {
    throw new Error('Product context not set. Call setProductContext() first.');
  }

  return {
    ...globalProductContext,
    ...eventSpecificData
  };
};

/**
 * Track an event in the dataLayer.
 * @param {Object} params - The parameters object.
 * @param {'personalisation_start' | 'personalisation_step_complete' | 'personalisation_complete' | 'slot_populated' | 'slot_removed' | 'add_on_added' | 'add_on_removed' | 'variant_selected'} params.eventType - The event type.
 * @param {EventData} params.data - The data to track.
 */
export const trackEvent = ({ eventType, data }) => {
  try {
    // Validate the input data using Valibot.
    const validationResult = validateEventData({ eventType, data });

    if (!validationResult.success) {
      // Extract specific validation errors for better debugging.
      const validationErrors = validationResult.issues.map((issue) => ({
        field: issue.path?.map((p) => p.key).join('.') || 'unknown',
        expected: issue.expected,
        received: issue.received,
        message: issue.message
      }));

      logError({
        label: 'Tracking',
        error: new Error(`Validation failed for event type: ${eventType}`),
        message: `Validation failed for event type: ${eventType}. Missing or invalid fields: ${validationErrors.map((e) => `${e.field} (expected: ${e.expected}, received: ${e.received})`).join(', ')}`,
        extraData: {
          eventType
        }
      });
      return;
    }

    const validatedData = validationResult.output;

    // Create base event data.
    const baseEventData = createBaseEventData(validatedData);

    // Get the appropriate transformer and apply it.
    const transformer = eventTransformers[`${eventType}`];
    if (!transformer) {
      throw new Error(`Event failed to transform`);
    }

    // Combine base data with event-specific data.
    const eventData = {
      ...baseEventData,
      ...transformer(validatedData)
    };

    window['dataLayer'] = window['dataLayer'] || [];

    // Push to dataLayer.
    window['dataLayer'].push({
      event: eventType,
      ...eventData
    });
  } catch (error) {
    logError({
      label: 'Tracking',
      error,
      message: `Tracking failed for event type: ${eventType}`,
      extraData: { eventType, data }
    });
  }
};

/**
 * Track personalisation start event.
 * @param {Object} params - The parameters object.
 * @param {number} params.totalSteps - Total number of personalisation steps.
 */
export const trackPersonalisationStart = ({ totalSteps }) => {
  const eventData = createEventData({
    productVariant: 'default',
    totalSteps
  });

  trackEvent({
    eventType: 'personalisation_start',
    data: eventData
  });
};

/**
 * Track personalisation step complete event.
 * @param {Object} params - The parameters object.
 * @param {number} params.stepNumber - The step number.
 * @param {string} params.stepName - The step name.
 * @param {number} params.totalSteps - Total number of steps.
 */
export const trackPersonalisationStepComplete = ({ stepNumber, stepName, totalSteps }) => {
  const eventData = createEventData({
    productVariant: globalProductContext?.productName || 'default',
    stepNumber,
    stepName,
    totalSteps
  });

  trackEvent({
    eventType: 'personalisation_step_complete',
    data: eventData
  });
};

/**
 * Track variant selected event.
 * @param {Object} params - The parameters object.
 * @param {string} params.variantId - The variant ID.
 * @param {number} params.variantPrice - The variant price.
 * @param {string} params.variantTitle - The variant title.
 */
export const trackVariantSelected = ({ variantId, variantPrice, variantTitle }) => {
  const eventData = createEventData({
    productVariant: variantTitle,
    variantId,
    variantPrice,
    variantTitle
  });

  trackEvent({
    eventType: 'variant_selected',
    data: eventData
  });
};

/**
 * Track slot populated event.
 * @param {Object} params - The parameters object.
 * @param {string} params.slotName - The slot name.
 * @param {string} params.slotType - The slot type.
 * @param {number} params.slotPosition - The slot position.
 * @param {string} params.slotValue - The slot value.
 * @param {number} params.stepNumber - The step number.
 * @param {string} params.stepName - The step name.
 */
export const trackSlotPopulated = ({ slotName, slotType, slotPosition, slotValue, stepNumber, stepName }) => {
  const eventData = createEventData({
    productVariant: globalProductContext?.productName || 'default',
    slotName,
    slotType,
    slotPosition,
    slotValue,
    stepNumber,
    stepName
  });

  trackEvent({
    eventType: 'slot_populated',
    data: eventData
  });
};

/**
 * Track slot removed event.
 * @param {Object} params - The parameters object.
 * @param {string} params.slotName - The slot name.
 * @param {string} params.slotType - The slot type.
 * @param {number} params.slotPosition - The slot position.
 * @param {number} params.stepNumber - The step number.
 * @param {string} params.stepName - The step name.
 */
export const trackSlotRemoved = ({ slotName, slotType, slotPosition, stepNumber, stepName }) => {
  const eventData = createEventData({
    productVariant: globalProductContext?.productName || 'default',
    slotName,
    slotType,
    slotPosition,
    stepNumber,
    stepName
  });

  trackEvent({
    eventType: 'slot_removed',
    data: eventData
  });
};

/**
 * Track personalisation complete event.
 * @param {Object} params - The parameters object.
 * @param {number} params.totalPrice - The total price.
 * @param {number} params.totalSteps - Total number of steps.
 */
export const trackPersonalisationComplete = ({ totalPrice, totalSteps }) => {
  const eventData = createEventData({
    productVariant: globalProductContext?.productName || 'default',
    totalPrice,
    totalSteps
  });

  trackEvent({
    eventType: 'personalisation_complete',
    data: eventData
  });
};
